import axios from 'axios';
import { API_URL } from './config';

// Cliente axios único y autenticado para todos los servicios.
// Antes cada service (budget, debt, report, subscription, voice,
// transactions) armaba su propia URL a mano y varios se olvidaban de
// mandar el token — por eso esas pantallas fallaban con 401 en
// silencio. Centralizando esto acá, se arregla una sola vez para todos.
//
// Nota importante: en Netlify, REACT_APP_API_URL ya viene con "/api/v1"
// al final (así quedó configurado). Si aquí le agregábamos "/api/v1" de
// nuevo sin más, la URL final quedaba duplicada
// (".../api/v1/api/v1/auth/register") y CADA endpoint que pasa por este
// cliente (registro, forgot/reset password, checkEmail) devolvía 404 en
// producción. Esto normaliza API_URL quitando un "/api/v1" final si ya
// viene incluido, para que funcione sin importar cómo esté seteada la
// variable de entorno.
const normalizedApiUrl = API_URL.replace(/\/api\/v1\/?$/, '');

// Raíz del backend SIN /api/v1 — se usa para pegarle a /health (que vive
// fuera del router versionado) al "despertar" el servidor gratuito de
// Render antes de que el usuario someta el login.
export const API_ROOT_URL = normalizedApiUrl;

const apiClient = axios.create({
  baseURL: `${normalizedApiUrl}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  timeout: 30000,
});

apiClient.interceptors.request.use(
  (config) => {
    // 3 ago 2026 (pedido de Augusto): el token vive en sessionStorage, no
    // en localStorage, a propósito -- sessionStorage se borra solo al
    // cerrar el navegador/pestaña, así que cerrar el navegador ya equivale
    // a cerrar sesión, sin depender solo del timer de 15 min de inactividad
    // (useIdleLogout.js), que no sobrevive a un reinicio del navegador.
    const token = sessionStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default apiClient;

// Se mantienen por compatibilidad, ahora sí apuntando al backend real.
export const checkEmail = async (email) => {
  const response = await apiClient.get(`/auth/check-email?email=${email}`);
  return response.data;
};

export const registerUser = async (userData) => {
  const response = await apiClient.post('/auth/register', userData);
  return response.data;
};

export const requestPasswordReset = async (email) => {
  const response = await apiClient.post('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await apiClient.post('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return response.data;
};
